import {
  type BookingShipmentActivity,
  TransitType,
  type BookingDetail,
  type ShipmentActivityFormPayload,
  type ShipmentActivityPayload,
  BookingStatus,
  shipmentActivitySchema,
} from '@dc-trans/data-layer';
import { usePreview } from '@/context/previewContext';
import { FuelPumpIcon } from '@/assets';
import { Button, Table, Tooltip } from 'pink-lava-ui';
import { Button as AntdButton, Descriptions, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { FileImageIcon, PlusSquareIcon, Trash2Icon } from 'lucide-react';
import { FormModal } from '@/components/ui/modal';
import { useState } from 'react';
import { useNotification } from '@/context/notificationContext';
import { useDeleteShipmentActivity, usePostCreateShipmentActivity } from '@/mutations';
import queryClient from '@/libs/queryClient';

const TransitListSection = ({
  activityList,
  booking,
}: {
  activityList: BookingShipmentActivity[];
  booking: BookingDetail;
}) => {
  const [formVisible, setFormVisible] = useState(false);
  const notification = useNotification();
  const preview = usePreview();
  const createShipmentActivityMutation = usePostCreateShipmentActivity({
    meta: {
      message: 'Gagal menambahkan data transit.',
    },
  });
  const deleteShipmentActivityMutation = useDeleteShipmentActivity({
    meta: {
      message: 'Gagal menghapus data transit.',
    },
  });
  const transitList = (activityList || []).filter(item => item.activity_type === 'transit');

  const handleSubmit = (values: ShipmentActivityFormPayload) => {
    const payload: ShipmentActivityPayload = {
      ...values,
      activity_type: 'transit',
    };
    createShipmentActivityMutation.mutate(
      {
        id: booking.ulid,
        payload,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['booking', booking.ulid],
          });
          notification.success({
            message: 'Berhasil menambahkan data transit',
          });
          setFormVisible(false);
        },
        onError: error => {
          console.error('Failed to add transit', error);
        },
      }
    );
  };

  return (
    <div className="flex gap-4 flex-col font-nunito">
      <div className="flex gap-2 items-center">
        <img src={FuelPumpIcon} className="w-5 h-5" />
        <p className="text-lg font-semibold">Transits</p>
      </div>
      {transitList.length > 0 && (
        <Table
          size="small"
          bordered
          dataSource={transitList}
          scroll={{ x: 800 }}
          columns={[
            {
              title: 'Tipe',
              dataIndex: 'activity_subtype',
              minWidth: 120,
              render: (value: string) => TransitType[value as keyof typeof TransitType],
            },
            {
              title: 'Lokasi',
              dataIndex: 'destination_name',
            },
            {
              title: 'Waktu Upload',
              dataIndex: 'activity_time',
              render: (value: string) => (value ? dayjs(value).format('DD MMM YYYY HH:mm:ss') : ''),
            },
            {
              title: 'Catatan',
              dataIndex: 'notes',
            },
            {
              title: 'Dibuat Oleh',
              dataIndex: 'created_by_name',
            },
            {
              title: 'Bukti Transit',
              dataIndex: 'attachments',
              align: 'center',
              render: (value, record) => {
                const bookingActivity = record as BookingShipmentActivity;
                return Array.isArray(value) && value.length > 0 ? (
                  <Tooltip title="Lihat Bukti Transit">
                    <Button
                      shape="circle"
                      size="small"
                      type="ghost"
                      onClick={() => {
                        preview({
                          fullscreen: true,
                          title: 'Bukti Transit',
                          content: value.map(item => item.url),
                          description: (
                            <Descriptions
                              column={1}
                              size="small"
                              className="font-nunito"
                              classNames={{
                                content: 'font-nunito-bold text-white! text-shadow-lg',
                                label: 'font-nunito-bold max-w-1/2 text-white! text-shadow-lg',
                              }}
                              items={[
                                {
                                  label: 'Waktu Transit',
                                  children: bookingActivity.activity_time
                                    ? dayjs(bookingActivity.activity_time).format('DD MMMM YYYY, HH:mm:ss')
                                    : '-',
                                },
                                {
                                  label: 'Catatan',
                                  children: bookingActivity.notes || '-',
                                },
                              ]}
                            />
                          ),
                        });
                      }}
                    >
                      <FileImageIcon size={16} />
                    </Button>
                  </Tooltip>
                ) : (
                  '-'
                );
              },
            },
            {
              title: 'Aksi',
              align: 'center',
              render: (_, record) => {
                return (
                  <Tooltip title="Hapus Transit">
                    <Popconfirm
                      title="Apakah yakin ingin menghapus transit ini?"
                      onConfirm={() => {
                        deleteShipmentActivityMutation.mutate(
                          {
                            bookingId: booking.ulid,
                            ids: [record.ulid],
                          },
                          {
                            onSuccess: () => {
                              notification.success({
                                message: 'Berhasil menghapus data transit',
                              });
                              queryClient.invalidateQueries({
                                queryKey: ['booking', booking.ulid],
                              });
                            },
                          }
                        );
                      }}
                    >
                      <Button
                        shape="circle"
                        size="small"
                        type="ghost"
                        hidden={booking.status === BookingStatus.Completed}
                      >
                        <Trash2Icon size={16} />
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                );
              },
            },
          ]}
        />
      )}
      <AntdButton
        hidden={booking.status === BookingStatus.Completed}
        block
        disabled={
          (booking.status === BookingStatus.New && !booking.assignment?.driver?.ulid) ||
          !booking.assignment?.driver?.ulid
        }
        variant="outlined"
        className="pr-1! pl-3! rounded-lg!"
        onClick={() => setFormVisible(true)}
      >
        <div className="flex flex-row w-full items-center">
          <div className="w-full flex-1 text-left text-gray-500 font-nunito">Isi Bensin & Makan Siang</div>
          <PlusSquareIcon color="#EB008B" />
        </div>
      </AntdButton>
      <FormModal<ShipmentActivityFormPayload>
        visible={formVisible}
        mode="create"
        validationSchema={shipmentActivitySchema}
        loading={createShipmentActivityMutation.isPending}
        initialValues={{
          attachment_ulids: [],
        }}
        title={'Transit'}
        fields={[
          {
            name: 'activity_subtype',
            label: 'Tipe Transit',
            type: 'select',
            placeholder: 'Pilih',
            required: true,
            options: Object.keys(TransitType).map(key => ({
              label: TransitType[key as keyof typeof TransitType],
              value: key,
            })),
          },
          {
            name: 'destination_ulid',
            label: 'Tujuan Pengantaran',
            placeholder: 'Pilih',
            required: true,
            type: 'select',
            options: [
              ...(booking.routes?.[0]
                ? [
                    {
                      label: booking.routes?.[0].origin_name!,
                      value: booking.routes?.[0].origin_ulid!,
                    },
                  ]
                : []),
              ...(booking.routes || []).map(item => ({
                label: item.destination_name,
                value: item.destination_ulid,
              })),
            ],
          },
          {
            name: 'attachment_ulids',
            label: 'Bukti Transit (Maks. 3 file)',
            maxConcurrentUploads: 3,
            type: 'attachment',
            required: true,
          },
          {
            name: 'notes',
            label: 'Catatan Tambahan',
            type: 'text',
            placeholder: 'Cth: makan siang sekalian isi bensin',
          },
        ]}
        onCancel={() => setFormVisible(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default TransitListSection;
